import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { t } from '../../hooks/useT';

interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
  isPremium: boolean;
  isBlocked: boolean;
  isDisabled: boolean;
  streak: number;
  eloRating: number;
  createdAt: string;
  totalRuns: number;
  officialWins: number;
  lastPlayedAt: string | null;
}

type PremiumFilter = '' | 'true' | 'false';

function premiumErrorMessage(err: unknown): string {
  const code = (err as { data?: { error?: string } })?.data?.error;
  if (code === 'cannot_modify_self') return t('admin.users.errSelf');
  if (code === 'cannot_revoke_admin_premium') return t('admin.users.errAdminPremium');
  if (code === 'user_not_found') return t('admin.users.errNotFound');
  return t('common.error');
}

export function AdminUsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [disabledUsers, setDisabledUsers] = useState<AdminUser[]>([]);
  const [filter, setFilter] = useState('');
  const [premiumFilter, setPremiumFilter] = useState<PremiumFilter>('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);

  const load = () => {
    api<{ users: AdminUser[]; disabledUsers?: AdminUser[] }>('/admin/users')
      .then((res) => {
        setUsers(res.users);
        setDisabledUsers(res.disabledUsers || []);
      })
      .catch(() => setError(t('common.error')));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = showDisabled ? disabledUsers : users;
    if (premiumFilter === 'true') list = list.filter((u) => u.isPremium);
    if (premiumFilter === 'false') list = list.filter((u) => !u.isPremium);
    if (!q) return list;
    return list.filter(
      (u) => u.email?.toLowerCase().includes(q)
        || u.displayName?.toLowerCase().includes(q),
    );
  }, [users, disabledUsers, filter, premiumFilter, showDisabled]);

  const patchUser = (userId: string, patch: Partial<AdminUser>) => {
    const apply = (list: AdminUser[]) => list.map((u) => (u.id === userId ? { ...u, ...patch } : u));
    setUsers(apply);
    setDisabledUsers(apply);
  };

  const runAction = async (
    userId: string,
    action: () => Promise<unknown>,
    onSuccess?: (res: unknown) => void,
    errorMsg?: (err: unknown) => string,
  ) => {
    setBusyId(userId);
    setError('');
    try {
      const res = await action();
      onSuccess?.(res);
      load();
    } catch (err) {
      setError(errorMsg ? errorMsg(err) : t('common.error'));
    } finally {
      setBusyId(null);
    }
  };

  const togglePremium = (user: AdminUser) => {
    const next = !user.isPremium;
    runAction(
      user.id,
      () => api('/admin/premium', {
        method: 'POST',
        body: JSON.stringify({ userId: user.id, premium: next }),
      }),
      () => patchUser(user.id, { isPremium: next }),
      premiumErrorMessage,
    );
  };

  const toggleBlock = (user: AdminUser) => runAction(user.id, () =>
    api('/admin/block', {
      method: 'POST',
      body: JSON.stringify({ userId: user.id, blocked: !user.isBlocked }),
    }));

  const disableUser = (user: AdminUser) => {
    if (!window.confirm(t('admin.users.deleteConfirm'))) return;
    runAction(user.id, () =>
      api('/admin/disable', {
        method: 'POST',
        body: JSON.stringify({ userId: user.id, disabled: true }),
      }));
  };

  const restoreUser = (user: AdminUser) => runAction(user.id, () =>
    api('/admin/disable', {
      method: 'POST',
      body: JSON.stringify({ userId: user.id, disabled: false }),
    }));

  if (error && !users.length && !disabledUsers.length) {
    return <p className="admin-msg">{error}</p>;
  }
  if (!users.length && !disabledUsers.length && !error) {
    return <p className="admin-msg">{t('common.loading')}</p>;
  }

  return (
    <div className="admin-users">
      <div className="admin-filters">
        <input
          type="search"
          className="admin-search"
          placeholder={t('admin.users.search')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <select
          value={premiumFilter}
          onChange={(e) => setPremiumFilter(e.target.value as PremiumFilter)}
          aria-label={t('admin.users.premiumFilterAll')}
        >
          <option value="">{t('admin.users.premiumFilterAll')}</option>
          <option value="true">{t('admin.users.premiumFilterYes')}</option>
          <option value="false">{t('admin.users.premiumFilterNo')}</option>
        </select>
        <button
          type="button"
          className={`btn secondary admin-btn-sm${showDisabled ? ' active' : ''}`}
          onClick={() => setShowDisabled((v) => !v)}
        >
          {showDisabled ? t('admin.users.showActive') : t('admin.users.showDeleted')}
          {!showDisabled && disabledUsers.length > 0 && ` (${disabledUsers.length})`}
        </button>
      </div>

      {error && <p className="admin-msg admin-msg-error">{error}</p>}

      {filtered.length === 0 ? (
        <p className="admin-msg">{t('admin.users.empty')}</p>
      ) : (
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.users.email')}</th>
                <th>{t('admin.users.name')}</th>
                <th>{t('admin.users.premium')}</th>
                <th>{t('admin.users.status')}</th>
                <th>{t('admin.users.runs')}</th>
                <th>{t('admin.users.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => {
                const busy = busyId === user.id;
                const modifiable = !user.isAdmin;

                return (
                  <tr
                    key={user.id}
                    className={[
                      user.isBlocked ? 'admin-row-blocked' : '',
                      user.isDisabled ? 'admin-row-disabled' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <td>{user.email}</td>
                    <td>
                      {user.displayName}
                      {user.isAdmin && (
                        <span className="admin-badge">{t('admin.users.admin')}</span>
                      )}
                    </td>
                    <td>
                      {modifiable ? (
                        <button
                          type="button"
                          className={`btn secondary admin-btn-sm admin-premium-toggle${user.isPremium ? ' active' : ''}`}
                          disabled={busy}
                          onClick={() => togglePremium(user)}
                          title={user.isPremium
                            ? t('admin.users.revokePremium')
                            : t('admin.users.grantPremium')}
                        >
                          {user.isPremium
                            ? t('admin.users.premiumYes')
                            : t('admin.users.premiumNo')}
                        </button>
                      ) : (
                        <span className="admin-muted-hint">{t('admin.users.adminLocked')}</span>
                      )}
                    </td>
                    <td>
                      <div className="admin-user-badges">
                        {user.isPremium && (
                          <span className="admin-badge admin-badge-premium">{t('admin.users.premium')}</span>
                        )}
                        {user.isBlocked && (
                          <span className="admin-badge admin-badge-danger">{t('admin.users.blocked')}</span>
                        )}
                        {user.isDisabled && (
                          <span className="admin-badge admin-badge-muted">{t('admin.users.deleted')}</span>
                        )}
                        {!user.isPremium && !user.isBlocked && !user.isDisabled && (
                          <span className="admin-muted-dash">—</span>
                        )}
                      </div>
                    </td>
                    <td>{user.totalRuns}</td>
                    <td>
                      {modifiable ? (
                        <div className="admin-user-actions">
                          {!user.isDisabled && (
                            <button
                              type="button"
                              className={`btn secondary admin-btn-sm${user.isBlocked ? ' admin-btn-warn' : ''}`}
                              disabled={busy}
                              onClick={() => toggleBlock(user)}
                            >
                              {user.isBlocked
                                ? t('admin.users.unblock')
                                : t('admin.users.block')}
                            </button>
                          )}
                          {user.isDisabled ? (
                            <button
                              type="button"
                              className="btn secondary admin-btn-sm"
                              disabled={busy}
                              onClick={() => restoreUser(user)}
                            >
                              {t('admin.users.restore')}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn ghost admin-btn-sm admin-btn-danger"
                              disabled={busy}
                              onClick={() => disableUser(user)}
                            >
                              {t('admin.users.delete')}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="admin-muted-hint">{t('admin.users.adminLocked')}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
